import { expectedOrganizationHeaders } from '@/store/expected-organization-headers';
import { crmApi } from './crm.feature';

/**
 * The native dialer: cleanse-api `/api/v1/telephony`. Settings and numbers
 * for Settings › Calling; each rep's own number. Behind `native_dialer_v1`
 * and the `dialer` plan feature on both sides. Every request is pinned to the
 * org it was made for.
 */

export interface CallingHours {
  /** 0 = Monday … 6 = Sunday. */
  days: number[];
  /** HH:MM, lead-local; never before 08:00 or after 21:00. */
  start: string;
  end: string;
}

export interface TelephonyUsage {
  creditsCharged: number;
  creditsHeld: number;
  minutes: number;
  calls: number;
}

export interface TelephonySettings {
  enabled: boolean;
  /** Every Twilio piece is in place: calls work once a rep has a number. */
  ready: boolean;
  status: 'active' | 'suspended' | 'closed' | null;
  statusReason?: string | null;
  verificationStatus?: string | null;
  verificationNote?: string | null;
  recordingEnabled: boolean;
  callingHours: CallingHours;
  monthlyCapCredits: number | null;
  usage: TelephonyUsage | null;
  /** The org's business address on its Twilio account, once saved. */
  address?: BusinessAddress | null;
  termsAcceptedAt?: string | null;
}

/** Most Canadian local numbers can only be bought with one on file. */
export interface BusinessAddress {
  businessName: string;
  street: string;
  city: string;
  /** Two-letter province or state (ON, NY). */
  region: string;
  postalCode: string;
  country: 'US' | 'CA';
}

/** What Twilio Trust Hub needs to verify the business (admins only). */
export interface BusinessIdentity {
  legalName: string;
  businessType: string;
  industry: string;
  registrationType: 'EIN' | 'DUNS' | 'CBN';
  registrationNumber: string;
  website: string;
  rep: {
    firstName: string;
    lastName: string;
    title: string;
    position: string;
    email: string;
    phone: string;
  };
  /** The name US landlines show; blank uses the business name. */
  cnamName?: string | null;
  useCase?: string | null;
  employeeCount?: number | null;
  dailyCalls?: number | null;
}

export interface Verification {
  identity: BusinessIdentity | null;
  hasAddress: boolean;
  verificationStatus: string | null;
  verificationNote: string | null;
  /** Twilio's review state per product: shaken, cnam, voice_integrity. */
  products?: Record<string, string>;
}

export interface SettingsUpdate {
  recordingEnabled?: boolean;
  callingHours?: CallingHours;
  monthlyCapCredits?: number;
}

export interface PhoneNumber {
  id: string;
  e164: string;
  country: string;
  region: string | null;
  locality: string | null;
  assignedUserId: string | null;
  callbackE164: string | null;
  status: 'active' | 'paused' | 'releasing' | 'released';
  releaseAfter: string | null;
  rentCredits: number;
  rentPaidThrough: string | null;
  /** The daily health check: is the number still getting answered? */
  healthStatus?: 'unknown' | 'healthy' | 'watch' | 'flagged' | null;
  answerRate7d?: number | null;
  healthCheckedAt?: string | null;
}

export interface PhoneNumbers {
  numbers: PhoneNumber[];
  /** What one more number costs a month: 0 while the seats include it. */
  nextNumberRentCredits: number;
}

export interface AvailableNumber {
  e164: string;
  country: string;
  region: string | null;
  locality: string | null;
  /**
   * Which address Twilio needs on file to sell it: anywhere (`any`), in its
   * country (`local`) or outside it (`foreign`).
   */
  addressRequirement?: 'none' | 'any' | 'local' | 'foreign';
  numberType?: NumberType;
  /** False outside the US and Canada until regulatory bundles exist. */
  buyable?: boolean;
}

export type NumberType = 'local' | 'mobile' | 'national' | 'toll_free';

export interface NumberCountry {
  country: string;
  name: string;
  types: NumberType[];
}

/** What Twilio needs before it sells numbers of a type in a country. */
export interface ComplianceRequirements {
  country: string;
  numberType: NumberType;
  needsBundle: boolean;
  regulations: {
    regulationSid: string | null;
    name: string | null;
    endUserFields: { field: string; label: string; description: string }[];
    documents: {
      requirement: string;
      name: string;
      description: string;
      accepted: { type: string; name: string }[];
    }[];
    addressRequired: boolean;
    addressRules: string[];
  }[];
}

/** One area code's outcome from buying pool numbers. */
export interface PoolResult {
  areaCode: string;
  status: 'bought' | 'covered' | 'no_stock' | 'failed' | 'skipped';
  /** The number bought, or why it failed or was skipped. */
  detail: string | null;
}

export interface MyNumber {
  enabled: boolean;
  ready: boolean;
  accountStatus: string | null;
  recordingEnabled: boolean;
  number: PhoneNumber | null;
}

export interface CallBlocked {
  code: string;
  message: string;
}

export interface VoiceToken {
  token?: string | null;
  identity?: string | null;
  expiresInSeconds?: number | null;
  blocked?: CallBlocked | null;
}

/** One call, as the API reads it back. */
export interface CallRecord {
  id: string;
  direction: 'outbound' | 'inbound' | 'voicemail';
  /** The rep who placed or took the call. */
  userId?: string | null;
  status: string;
  blockReason: string | null;
  fromE164: string;
  toE164: string;
  leadId: string | null;
  taskId: string | null;
  createdAt: string | null;
  answeredAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  billedMinutes: number | null;
  creditsCharged: number;
  /** Parallel dialing: whether a person answered with no rep free. */
  abandoned?: boolean;
  /** Twilio's answering-machine verdict on a parallel leg. */
  answeredBy?: string | null;
  /** Credits a connected minute costs (more for some countries). */
  rateCredits?: number;
  maxMinutes: number;
  final: boolean;
  disposition: string | null;
  notes: string | null;
  saved: boolean;
  hasRecording: boolean;
  recordingSeconds?: number | null;
  /** Kept after the recording is deleted (90 days). */
  transcript?: string | null;
}

/** Reports → Calls: totals for the range and its newest calls. */
export interface CallReport {
  totals: {
    /** Outbound calls placed. */
    calls: number;
    /** Of those, answered. */
    connected: number;
    inbound: number;
    talkSeconds: number;
    credits: number;
  };
  calls: CallRecord[];
  /** More calls in the range than the list holds; the totals count them all. */
  truncated: boolean;
}

/** A rep's parallel dialing session: their browser waits in its room while
 *  batches of leads ring; the first to answer joins them. */
export interface DialSession {
  id: string;
  status: 'open' | 'closed';
  /** The rep's browser has joined the room. */
  inRoom: boolean;
  maxLines: number;
  /** One while too many recent calls were answered with no rep free. */
  linesAllowed: number;
  abandonRate: number;
  connectedCallId: string | null;
  batchId: string | null;
  legs: CallRecord[];
}

export interface DialLead {
  to: string;
  leadId?: string;
  taskId?: string;
}

export interface DialBatch {
  batchId: string | null;
  lines: number;
  abandonRate: number;
  legs: CallRecord[];
  /** Leads not rung now: not_dialed (over the lines) stays queued. */
  skipped: (DialLead & { reason: string })[];
  blocked?: CallBlocked | null;
}

export interface StartCallResult {
  call?: CallRecord | null;
  blocked?: CallBlocked | null;
}

const headers = expectedOrganizationHeaders;
const ORG = (organizationId: string) => ({
  type: 'Telephony' as const,
  id: organizationId,
});

export const telephonyApi = crmApi
  .enhanceEndpoints({ addTagTypes: ['Telephony'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      getTelephonySettings: builder.query<TelephonySettings, string>({
        query: (organizationId) => ({
          url: '/telephony/settings',
          headers: headers(organizationId),
        }),
        providesTags: (_result, _error, organizationId) => [
          ORG(organizationId),
        ],
      }),
      enableCalling: builder.mutation<TelephonySettings, string>({
        query: (organizationId) => ({
          url: '/telephony/enable',
          method: 'POST',
          headers: headers(organizationId),
          body: { acceptTerms: true },
        }),
        invalidatesTags: (_result, error, organizationId) =>
          error ? [] : [ORG(organizationId)],
      }),
      updateTelephonySettings: builder.mutation<
        TelephonySettings,
        { organizationId: string; body: SettingsUpdate }
      >({
        query: ({ organizationId, body }) => ({
          url: '/telephony/settings',
          method: 'PUT',
          headers: headers(organizationId),
          body,
        }),
        invalidatesTags: (_result, error, { organizationId }) =>
          error ? [] : [ORG(organizationId)],
      }),
      setBusinessAddress: builder.mutation<
        TelephonySettings,
        { organizationId: string; body: BusinessAddress }
      >({
        query: ({ organizationId, body }) => ({
          url: '/telephony/address',
          method: 'PUT',
          headers: headers(organizationId),
          body,
        }),
        invalidatesTags: (_result, error, { organizationId }) =>
          error ? [] : [ORG(organizationId)],
      }),
      getVerification: builder.query<Verification, string>({
        query: (organizationId) => ({
          url: '/telephony/verification',
          headers: headers(organizationId),
        }),
        providesTags: (_result, _error, organizationId) => [
          ORG(organizationId),
        ],
      }),
      setBusinessIdentity: builder.mutation<
        Verification,
        { organizationId: string; body: BusinessIdentity }
      >({
        query: ({ organizationId, body }) => ({
          url: '/telephony/verification',
          method: 'PUT',
          headers: headers(organizationId),
          body,
        }),
        invalidatesTags: (_result, error, { organizationId }) =>
          error ? [] : [ORG(organizationId)],
      }),
      // Send the saved identity to Twilio Trust Hub for review.
      submitVerification: builder.mutation<Verification, string>({
        query: (organizationId) => ({
          url: '/telephony/verification/submit',
          method: 'POST',
          headers: headers(organizationId),
        }),
        invalidatesTags: (_result, error, organizationId) =>
          error ? [] : [ORG(organizationId)],
      }),
      listPhoneNumbers: builder.query<PhoneNumbers, string>({
        query: (organizationId) => ({
          url: '/telephony/numbers',
          headers: headers(organizationId),
        }),
        providesTags: (_result, _error, organizationId) => [
          ORG(organizationId),
        ],
      }),
      searchAvailableNumbers: builder.query<
        AvailableNumber[],
        {
          organizationId: string;
          country: string;
          areaCode?: string;
          region?: string;
          numberType?: NumberType;
          contains?: string;
        }
      >({
        query: ({
          organizationId,
          country,
          areaCode,
          region,
          numberType,
          contains,
        }) => ({
          url: '/telephony/numbers/available',
          headers: headers(organizationId),
          params: {
            country,
            ...(areaCode ? { areaCode } : {}),
            ...(region ? { region } : {}),
            ...(numberType ? { numberType } : {}),
            ...(contains ? { contains } : {}),
          },
        }),
        // A search is a moment's stock: never served from cache.
        keepUnusedDataFor: 0,
      }),
      /** Other area codes in the same state or province with numbers the
       * org can buy now, most stock first. */
      getNearbyAreaCodes: builder.query<
        { areaCodes: string[] },
        { organizationId: string; country: 'US' | 'CA'; areaCode?: string }
      >({
        query: ({ organizationId, country, areaCode }) => ({
          url: '/telephony/numbers/nearby-area-codes',
          headers: headers(organizationId),
          params: { country, areaCode },
        }),
        keepUnusedDataFor: 60,
      }),
      buyPhoneNumber: builder.mutation<
        PhoneNumber,
        { organizationId: string; e164: string; assignedUserId?: string }
      >({
        query: ({ organizationId, e164, assignedUserId }) => ({
          url: '/telephony/numbers',
          method: 'POST',
          headers: headers(organizationId),
          body: { e164, ...(assignedUserId ? { assignedUserId } : {}) },
        }),
        invalidatesTags: (_result, error, { organizationId }) =>
          error ? [] : [ORG(organizationId)],
      }),
      updatePhoneNumber: builder.mutation<
        PhoneNumber,
        {
          organizationId: string;
          id: string;
          /** null takes the number back from its rep. */
          assignedUserId?: string | null;
          callbackE164?: string | null;
        }
      >({
        query: ({ organizationId, id, ...body }) => ({
          url: `/telephony/numbers/${id}`,
          method: 'PATCH',
          headers: headers(organizationId),
          body,
        }),
        invalidatesTags: (_result, error, { organizationId }) =>
          error ? [] : [ORG(organizationId)],
      }),
      releasePhoneNumber: builder.mutation<
        PhoneNumber,
        { organizationId: string; id: string }
      >({
        query: ({ organizationId, id }) => ({
          url: `/telephony/numbers/${id}`,
          method: 'DELETE',
          headers: headers(organizationId),
        }),
        invalidatesTags: (_result, error, { organizationId }) =>
          error ? [] : [ORG(organizationId)],
      }),
      replacePhoneNumber: builder.mutation<
        PhoneNumber,
        { organizationId: string; id: string }
      >({
        query: ({ organizationId, id }) => ({
          url: `/telephony/numbers/${id}/replace`,
          method: 'POST',
          headers: headers(organizationId),
        }),
        invalidatesTags: (_result, error, { organizationId }) =>
          error ? [] : [ORG(organizationId)],
      }),
      buyPoolNumbers: builder.mutation<
        { results: PoolResult[] },
        {
          organizationId: string;
          body: { areaCodes: string[] } | { top: number };
        }
      >({
        query: ({ organizationId, body }) => ({
          url: '/telephony/numbers/pool',
          method: 'POST',
          headers: headers(organizationId),
          body,
        }),
        invalidatesTags: (_result, error, { organizationId }) =>
          error ? [] : [ORG(organizationId)],
      }),
      openDialSession: builder.mutation<
        DialSession,
        { organizationId: string; maxLines: number }
      >({
        query: ({ organizationId, maxLines }) => ({
          url: '/telephony/dial-sessions',
          method: 'POST',
          headers: headers(organizationId),
          body: { maxLines },
        }),
      }),
      getDialSession: builder.query<
        DialSession,
        { organizationId: string; id: string }
      >({
        query: ({ organizationId, id }) => ({
          url: `/telephony/dial-sessions/${id}`,
          headers: headers(organizationId),
        }),
      }),
      nextDialBatch: builder.mutation<
        DialBatch,
        { organizationId: string; id: string; leads: DialLead[] }
      >({
        query: ({ organizationId, id, leads }) => ({
          url: `/telephony/dial-sessions/${id}/next`,
          method: 'POST',
          headers: headers(organizationId),
          body: { leads },
        }),
      }),
      endDialSession: builder.mutation<
        DialSession,
        { organizationId: string; id: string }
      >({
        query: ({ organizationId, id }) => ({
          url: `/telephony/dial-sessions/${id}/end`,
          method: 'POST',
          headers: headers(organizationId),
        }),
      }),
      getNumberCountries: builder.query<{ countries: NumberCountry[] }, string>(
        {
          query: (organizationId) => ({
            url: '/telephony/numbers/countries',
            headers: headers(organizationId),
          }),
        }
      ),
      getCountryPrices: builder.query<
        { country: string; prices: { type: NumberType; monthlyUsd: string }[] },
        { organizationId: string; country: string }
      >({
        query: ({ organizationId, country }) => ({
          url: `/telephony/numbers/countries/${country}`,
          headers: headers(organizationId),
        }),
      }),
      getComplianceRequirements: builder.query<
        ComplianceRequirements,
        { organizationId: string; country: string; numberType: NumberType }
      >({
        query: ({ organizationId, country, numberType }) => ({
          url: '/telephony/compliance/requirements',
          headers: headers(organizationId),
          params: { country, numberType, endUserType: 'business' },
        }),
      }),
      getMyNumber: builder.query<MyNumber, string>({
        query: (organizationId) => ({
          url: '/telephony/me',
          headers: headers(organizationId),
        }),
        providesTags: (_result, _error, organizationId) => [
          ORG(organizationId),
        ],
      }),
      setMyCallback: builder.mutation<
        MyNumber,
        { organizationId: string; callbackE164: string | null }
      >({
        query: ({ organizationId, callbackE164 }) => ({
          url: '/telephony/me/callback',
          method: 'PUT',
          headers: headers(organizationId),
          body: { callbackE164 },
        }),
        invalidatesTags: (_result, error, { organizationId }) =>
          error ? [] : [ORG(organizationId)],
      }),
      getVoiceToken: builder.mutation<VoiceToken, string>({
        query: (organizationId) => ({
          url: '/telephony/token',
          method: 'POST',
          headers: headers(organizationId),
        }),
      }),
      startCall: builder.mutation<
        StartCallResult,
        { organizationId: string; to: string; taskId?: string; leadId?: string }
      >({
        query: ({ organizationId, to, taskId, leadId }) => ({
          url: '/telephony/calls',
          method: 'POST',
          headers: headers(organizationId),
          body: {
            to,
            ...(taskId ? { taskId } : {}),
            ...(leadId ? { leadId } : {}),
          },
        }),
      }),
      getCallState: builder.query<
        CallRecord,
        { organizationId: string; id: string }
      >({
        query: ({ organizationId, id }) => ({
          url: `/telephony/calls/${id}`,
          headers: headers(organizationId),
        }),
        // Read fresh each time: the call is still finishing.
        keepUnusedDataFor: 0,
      }),
      cancelCall: builder.mutation<
        CallRecord,
        { organizationId: string; id: string }
      >({
        query: ({ organizationId, id }) => ({
          url: `/telephony/calls/${id}/cancel`,
          method: 'POST',
          headers: headers(organizationId),
        }),
      }),
      listCallsForLead: builder.query<
        CallRecord[],
        { organizationId: string; leadId: string }
      >({
        query: ({ organizationId, leadId }) => ({
          url: '/telephony/calls',
          headers: headers(organizationId),
          params: { leadId },
        }),
        providesTags: (_result, _error, { organizationId }) => [
          { type: 'Telephony', id: `calls:${organizationId}` },
        ],
      }),
      // The signed-in rep's own calls, with or without a person.
      listMyCalls: builder.query<CallRecord[], string>({
        query: (organizationId) => ({
          url: '/telephony/calls',
          headers: headers(organizationId),
        }),
        providesTags: (_result, _error, organizationId) => [
          { type: 'Telephony', id: `calls:${organizationId}` },
        ],
      }),
      getCallReport: builder.query<
        CallReport,
        { organizationId: string; start: string; end: string; userId?: string }
      >({
        query: ({ organizationId, start, end, userId }) => ({
          url: '/telephony/calls/report',
          headers: headers(organizationId),
          params: { start, end, userId },
        }),
        providesTags: (_result, _error, { organizationId }) => [
          { type: 'Telephony', id: `calls:${organizationId}` },
        ],
      }),
      listUnsavedCalls: builder.query<CallRecord[], string>({
        query: (organizationId) => ({
          url: '/telephony/calls/unsaved',
          headers: headers(organizationId),
        }),
        providesTags: (_result, _error, organizationId) => [
          { type: 'Telephony', id: `unsaved:${organizationId}` },
        ],
      }),
      wrapUpCall: builder.mutation<
        CallRecord,
        {
          organizationId: string;
          id: string;
          disposition: string;
          notes?: string;
          addToDoNotCall?: boolean;
        }
      >({
        query: ({ organizationId, id, ...body }) => ({
          url: `/telephony/calls/${id}/wrap-up`,
          method: 'POST',
          headers: headers(organizationId),
          body,
        }),
        invalidatesTags: (_result, error, { organizationId }) =>
          error
            ? []
            : [
                { type: 'Telephony', id: `unsaved:${organizationId}` },
                { type: 'Telephony', id: `calls:${organizationId}` },
              ],
      }),
    }),
  });

export const {
  useGetTelephonySettingsQuery,
  useEnableCallingMutation,
  useUpdateTelephonySettingsMutation,
  useSetBusinessAddressMutation,
  useListPhoneNumbersQuery,
  useLazySearchAvailableNumbersQuery,
  useGetVerificationQuery,
  useSubmitVerificationMutation,
  useSetBusinessIdentityMutation,
  useGetNearbyAreaCodesQuery,
  useBuyPhoneNumberMutation,
  useUpdatePhoneNumberMutation,
  useBuyPoolNumbersMutation,
  useGetNumberCountriesQuery,
  useGetCountryPricesQuery,
  useGetComplianceRequirementsQuery,
  useOpenDialSessionMutation,
  useGetDialSessionQuery,
  useNextDialBatchMutation,
  useEndDialSessionMutation,
  useReleasePhoneNumberMutation,
  useReplacePhoneNumberMutation,
  useGetMyNumberQuery,
  useSetMyCallbackMutation,
  useGetVoiceTokenMutation,
  useStartCallMutation,
  useLazyGetCallStateQuery,
  useCancelCallMutation,
  useListUnsavedCallsQuery,
  useListCallsForLeadQuery,
  useListMyCallsQuery,
  useGetCallReportQuery,
  useWrapUpCallMutation,
} = telephonyApi;
